import { indexOf } from 'lodash';
import React, { useState } from 'react';

import { useOutsideClick } from 'commons/hooks';
import { useActiveTooltip, useTooltipChildren } from 'craft/components/Tooltip';
import {
  FloatingMenuActions,
  FloatingMenuContext,
  FloatingMenuNav,
} from 'craft/containers/FloatingMenu';
import { useAddTooltipStep } from 'craft/hooks';
import { TooltipType } from 'craft/types/TooltipType';

import { FLOATING_MENU_CUSTOMIZATION } from './FloatingMenuCustomization';
import { TooltipTab } from './TooltipTab';
import { TooltipTabContextMenu } from './TooltipTabContextMenu';

export type TabContext = {
  dom: HTMLElement | null;
  id: string;
  index: number;
};

interface Props {
  contentType: TooltipType;
  menuPosition: 'top' | 'bottom';
  onToggleMenuPosition: VoidFunction;
}

export const TooltipFloatingMenu = React.memo(
  ({ menuPosition, onToggleMenuPosition, contentType }: Props) => {
    const tooltips = useTooltipChildren();
    const activeTooltip = useActiveTooltip();
    const { addSubsequent: addTooltip } = useAddTooltipStep(
      contentType,
      activeTooltip?.data?.props?.variant,
    );

    const [tooltipTabContext, setTooltipTabContext] = useState<TabContext | null>(null);

    useOutsideClick(tooltipTabContext?.dom || null, () => {
      setTooltipTabContext(null);
    });

    const [floatingContextOpen, setFloatingContextOpen] = useState(false);

    useOutsideClick(document.getElementById('floating-menu-context-button') || null, () => {
      setFloatingContextOpen(false);
    });

    const [visibleTabs, setVisibleTabs] = useState<string[]>(tooltips);

    if (!tooltips) {
      return null;
    }

    return (
      <>
        <FloatingMenuNav
          tabIds={tooltips}
          activeTabIndex={activeTooltip?.id ? tooltips.indexOf(activeTooltip.id) : undefined}
          setVisibleTabs={(tabs) => setVisibleTabs(tabs)}
        >
          {visibleTabs.map((tooltipId) => {
            const tabIndex = indexOf(tooltips, tooltipId);
            return (
              <TooltipTab
                id={`floating-menu-tab-${tabIndex}`}
                // eslint-disable-next-line react/no-array-index-key
                key={`floating-menu-tab-${tooltipId}-${tabIndex}`}
                nodeId={tooltipId}
                contentType={contentType}
                tooltipPlacement={menuPosition}
                isContextOpen={tooltipTabContext?.id}
                onContextClick={(dom: HTMLButtonElement | null) => {
                  if (floatingContextOpen) {
                    setFloatingContextOpen(false);
                  }

                  if (tooltipTabContext?.id !== tooltipId) {
                    setTooltipTabContext({
                      id: tooltipId,
                      dom,
                      index: tabIndex,
                    });
                  }
                }}
              />
            );
          })}
        </FloatingMenuNav>

        <FloatingMenuActions
          onAdd={addTooltip}
          addLabel={FLOATING_MENU_CUSTOMIZATION[contentType].addLabel}
          // Temporarily add button directly in floating menu actions until there are more actions to add to context menu
          menuPosition={menuPosition}
          onToggleMenuPosition={() => {
            if (tooltipTabContext) {
              setTooltipTabContext(null);
            }
            onToggleMenuPosition();
          }}
          // onContextClick={() => {
          //   setTooltipTabContext(null);
          //   setFloatingContextOpen(true);
          // }}
        />

        {floatingContextOpen && (
          <FloatingMenuContext
            dom={document.getElementById('floating-menu-context-button-wrapper')}
            menuPosition={menuPosition}
            onToggleMenuPosition={onToggleMenuPosition}
          />
        )}

        {tooltipTabContext && (
          <TooltipTabContextMenu
            tooltipTabContext={tooltipTabContext}
            menuPosition={menuPosition}
          />
        )}
      </>
    );
  },
);

TooltipFloatingMenu.displayName = 'HotspotFloatingMenu';
